/**
 * Article Category Form Component
 * DS-Wiki Knowledge Share Module
 */

import { WIKI_POPUP_GRADIENT } from '@/modules/core/wiki/constants/popupHeader';
import { useState, useEffect } from 'react';
import dsWikiService from '../../services/dsWikiService';
import type { ArticleCategory, ArticleCategoryInput, ArticleType } from '../../services/types';
import { useToast } from '../../../../../hooks/useToast';
import { FormModal, FormInput, FormSelect, ChangeHistory } from '../../../../../components/base';

interface ArticleCategoryFormProps {
  isOpen: boolean;
  category: ArticleCategory | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  artcl_ctgry_nm: string;
  artcl_ctgry_cd: string;
  dscn_tx: string;
  icon_tx: string;
  clr_cd: string;
  rfrnc_prfx_tx: string;
  sqnce_nu: number;
  dswiki_artcl_type_id: number | null;
  a_in: boolean;
}

export default function ArticleCategoryForm({ isOpen, category, onClose, onSuccess }: ArticleCategoryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success } = useToast();
  const [articleTypes, setArticleTypes] = useState<ArticleType[]>([]);
  const [formData, setFormData] = useState<FormData>({
    artcl_ctgry_nm: '',
    artcl_ctgry_cd: '',
    dscn_tx: '',
    icon_tx: '',
    clr_cd: '',
    rfrnc_prfx_tx: '',
    sqnce_nu: 0,
    dswiki_artcl_type_id: null,
    a_in: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    fetchArticleTypes();
  }, []);

  useEffect(() => {
    if (category) {
      setFormData({
        artcl_ctgry_nm: category.artcl_ctgry_nm || '',
        artcl_ctgry_cd: category.artcl_ctgry_cd || '',
        dscn_tx: category.dscn_tx || '',
        icon_tx: category.icon_tx || '',
        clr_cd: category.clr_cd || '',
        rfrnc_prfx_tx: category.rfrnc_prfx_tx || '',
        sqnce_nu: category.sqnce_nu || 0,
        dswiki_artcl_type_id: category.dswiki_artcl_type_id || null,
        a_in: category.a_in !== false,
      });
    } else {
      setFormData({
        artcl_ctgry_nm: '',
        artcl_ctgry_cd: '',
        dscn_tx: '',
        icon_tx: '',
        clr_cd: '',
        rfrnc_prfx_tx: '',
        sqnce_nu: 0,
        dswiki_artcl_type_id: null,
        a_in: true,
      });
    }
    setErrors({});
    setApiError(null);
  }, [category, isOpen]);

  const fetchArticleTypes = async () => {
    try {
      const response = await dsWikiService.getActiveArticleTypes();
      setArticleTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching article types:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof FormData]) setErrors(prev => ({ ...prev, [name]: undefined }));
    if (apiError) setApiError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.artcl_ctgry_nm.trim()) newErrors.artcl_ctgry_nm = 'Category name is required';
    else if (formData.artcl_ctgry_nm.length > 100) newErrors.artcl_ctgry_nm = 'Name must be less than 100 characters';
    if (!formData.artcl_ctgry_cd.trim()) newErrors.artcl_ctgry_cd = 'Code is required';
    else if (formData.artcl_ctgry_cd.length > 20) newErrors.artcl_ctgry_cd = 'Code must be less than 20 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSave: ArticleCategoryInput = {
        artcl_ctgry_nm: formData.artcl_ctgry_nm,
        artcl_ctgry_cd: formData.artcl_ctgry_cd,
        dscn_tx: formData.dscn_tx || undefined,
        icon_tx: formData.icon_tx || undefined,
        clr_cd: formData.clr_cd || undefined,
        rfrnc_prfx_tx: formData.rfrnc_prfx_tx || undefined,
        sqnce_nu: formData.sqnce_nu,
        dswiki_artcl_type_id: formData.dswiki_artcl_type_id || undefined,
        a_in: formData.a_in,
      };
      if (category?.dswiki_artcl_ctgry_id) dataToSave.dswiki_artcl_ctgry_id = category.dswiki_artcl_ctgry_id;
      await dsWikiService.saveArticleCategory(dataToSave);
      success(category ? 'Category updated successfully!' : 'Category created successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving category:', error);
      setApiError(error?.message || 'Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal popup={{ gradient: WIKI_POPUP_GRADIENT, icon: 'ri-folders-line' }} isOpen={isOpen} onClose={onClose} onSubmit={handleSubmit} title={category ? 'Edit Article Category' : 'Add New Article Category'} submitText={category ? 'Update' : 'Create'} submitColor="amber" isSubmitting={isSubmitting}>
      {apiError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
          <div className="text-sm text-red-700">{apiError}</div>
        </div>
      )}

      <FormInput label="Category Name" name="artcl_ctgry_nm" value={formData.artcl_ctgry_nm} onChange={handleInputChange} placeholder="e.g., Municipal Acts, General Circulars" required error={errors.artcl_ctgry_nm} />
      <FormInput label="Code" name="artcl_ctgry_cd" value={formData.artcl_ctgry_cd} onChange={handleInputChange} placeholder="e.g., MACT, GCIR" required helperText="Unique code for the category" error={errors.artcl_ctgry_cd} disabled={!!category} />

      <FormSelect
        label="Article Type"
        name="dswiki_artcl_type_id"
        value={formData.dswiki_artcl_type_id?.toString() || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, dswiki_artcl_type_id: e.target.value ? parseInt(e.target.value) : null }))}
        options={[
          { value: '', label: 'Select Article Type' },
          ...articleTypes.map(t => ({ value: t.dswiki_artcl_type_id.toString(), label: t.artcl_type_nm }))
        ]}
        helperText="Optionally link to a specific article type"
      />

      <FormInput label="Reference Prefix" name="rfrnc_prfx_tx" value={formData.rfrnc_prfx_tx} onChange={handleInputChange} placeholder="e.g., GO-, CIR-" helperText="Prefix for reference numbers" />
      <FormInput label="Description" name="dscn_tx" value={formData.dscn_tx} onChange={handleInputChange} placeholder="Brief description" />

      <div className="grid grid-cols-2 gap-4">
        <FormInput label="Icon Class" name="icon_tx" value={formData.icon_tx} onChange={handleInputChange} placeholder="e.g., ri-file-text-line" helperText="Remix Icon class" />
        <FormInput label="Color" name="clr_cd" type="color" value={formData.clr_cd || '#6B7280'} onChange={handleInputChange} />
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <FormInput label="Sequence" name="sqnce_nu" type="number" value={formData.sqnce_nu.toString()} onChange={handleInputChange} placeholder="Display order" />
        <label className="flex items-center gap-2 cursor-pointer mt-8">
          <input type="checkbox" checked={formData.a_in} onChange={(e) => setFormData(prev => ({ ...prev, a_in: e.target.checked }))} className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500" />
          <span className="text-sm text-gray-700">Active</span>
        </label>
      </div>

      {category && <ChangeHistory lastChange={(category as any).lastChange} />}
    </FormModal>
  );
}
